import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Inject, Input, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {EmTranslatePipe, TranslationService} from '@emma-labs/shared/angular';
import {
  AGE_CHILDREN_OPTIONS,
  CIVIL_STATUS_OPTIONS,
  CivilStatus,
  convertJobCategoriesToDropdownOptions,
  CurrentProtection,
  ENVIRONMENT,
  EnvironmentBroker,
  Fna,
  HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS,
  NUM_CHILDREN_OPTIONS,
  PossibleAges,
  randomInt,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {TooltipModule} from 'primeng/tooltip';
import {debounceTime} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {BaseSectionComponent} from '../../base-section/base-section.component';
import {TextIndicatorComponent} from '../../text-indicator/text-indicator.component';
import {CurrentProtectionsSectionComponent} from './current-protections-section/current-protections-section.component';
import {NeedsSectionComponent} from './needs-section/needs-section.component';

interface FnaSectionForm {
  civilStatus: FormControl<CivilStatus | null>;
  numChildren: FormControl<number | null>;
  youngestChildAgeInMonths: FormControl<number | null>;
  salary: FormControl<number | null>;
  mortgageAmount: FormControl<number | null>;
  debtAmount: FormControl<number | null>;
  savingsAmount: FormControl<number | null>;
  heritageAmount: FormControl<number | null>;
  jobCategoryId: FormControl<number | null>;
  monthlyDebtPaymentAmount: FormControl<number | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-fna-section',
  templateUrl: './fna-section.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    DropdownModule,
    NgxMaskDirective,
    CurrentProtectionsSectionComponent,
    NeedsSectionComponent,
    InputTextModule,
    TooltipModule,
    TextIndicatorComponent,
    EmTranslatePipe,
  ],
})
export class FnaSectionComponent extends BaseSectionComponent<FormGroup<FnaSectionForm>, Fna> {
  readonly civilStatusOptions = CIVIL_STATUS_OPTIONS;
  readonly numChildrenOptions = NUM_CHILDREN_OPTIONS;
  readonly youngestChildOptions = AGE_CHILDREN_OPTIONS;
  readonly jobCategoryOptions = convertJobCategoriesToDropdownOptions();

  fna: Fna | undefined;
  isFr: boolean;
  currentProtections: CurrentProtection[] = [];
  protectionSearchResult: CurrentProtection | undefined;
  jobRiskClass: string | undefined;

  @Input({required: true})
  override set props(
    fna: (Fna & {protectionSearchResult?: CurrentProtection; jobCategoryId?: number}) | undefined,
  ) {
    if (!this.isPropsReceived && fna) {
      if (fna.numChildren && fna.numChildren > 0) {
        this.form.controls.youngestChildAgeInMonths.enable({emitEvent: false});
        this.form.controls.youngestChildAgeInMonths.addValidators(Validators.required);
      }
      super.props = fna;

      this.currentProtections = fna.currentProtections ?? [];
    }
    this.protectionSearchResult = fna?.protectionSearchResult;
    this.fna = fna;
    if (fna?.jobCategoryId) {
      const jobRiskClass = HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS[fna.jobCategoryId];
      this.jobRiskClass = this.translate.getTranslation(jobRiskClass) || jobRiskClass;
    }
  }

  @Output() policyNumberChanged = new EventEmitter<{
    output: string;
    callback?: (status: boolean) => void;
  }>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly translate: TranslationService,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
    this.isFr = translate.currentLanguageIsFr();
  }

  handleCurrentProtectionSectionOutput($event: CurrentProtection[]) {
    this.currentProtections = $event;
    this.propsChange.emit({output: {...this.formToOutput(), currentProtections: this.currentProtections}});
  }

  handlePolicyNumberChanged($event: {output: string; callback?: (status: boolean) => void}) {
    this.policyNumberChanged.emit($event);
  }

  simulateFna(): void {
    const numChildren = randomInt(1, 12);

    this.form.setValue({
      civilStatus: Math.random() > 0.5 ? CivilStatus.MARRIED : CivilStatus.SINGLE,
      numChildren,
      youngestChildAgeInMonths:
        numChildren > 0
          ? Math.random() > 0.5
            ? PossibleAges.ZERO_TWELVE_MONTHS
            : PossibleAges.TWENTY_FIVE_OR_MORE
          : null,
      salary: randomInt(0, 1_000_000),
      mortgageAmount: randomInt(0, 1_000_000),
      debtAmount: randomInt(0, 500_000),
      savingsAmount: randomInt(0, 500_000),
      jobCategoryId: randomInt(1, Object.keys(HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS).length),
      heritageAmount: randomInt(0, 500_000),
      monthlyDebtPaymentAmount: randomInt(0, 3_000),
    });
  }

  private createForm(): FormGroup<FnaSectionForm> {
    const form = this.fb.group<FnaSectionForm>({
      civilStatus: this.fb.control(null, [Validators.required]),
      numChildren: this.fb.control(null, [Validators.required]),
      youngestChildAgeInMonths: this.fb.control({value: null, disabled: true}),
      jobCategoryId: this.fb.control(null, [Validators.required]),
      salary: this.fb.control(null, [Validators.required]),
      mortgageAmount: this.fb.control(null, [Validators.required]),
      debtAmount: this.fb.control(null, [Validators.required]),
      savingsAmount: this.fb.control(null, [Validators.required]),
      heritageAmount: this.fb.control(null, [Validators.required]),
      monthlyDebtPaymentAmount: this.fb.control(null, [Validators.required]),
    });

    form.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe((_) => {
        this.propsChange.emit({
          output: {...this.formToOutput(), currentProtections: this.currentProtections},
        });
      });

    form.controls.numChildren.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe((numChildren) => {
        if (numChildren) {
          this.form.controls.youngestChildAgeInMonths.enable({emitEvent: false});
          this.form.controls.youngestChildAgeInMonths.addValidators(Validators.required);
        } else {
          this.form.controls.youngestChildAgeInMonths.reset(null, {emitEvent: false});
          this.form.controls.youngestChildAgeInMonths.disable({emitEvent: false});
        }
      });

    return form;
  }
}
